import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Button,
  Chip,
  Spinner,
} from '@material-tailwind/react';
import { PlusIcon, PencilIcon, TrashIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

const formatCurrency = (v) =>
  v != null && v !== '' ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v) : '—';

const formatDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const CONFIDENCE_COLORS = {
  High: 'green',
  Medium: 'amber',
  Low: 'red',
};

const EMPTY_ENTRY = {
  date: new Date().toISOString().split('T')[0],
  source: 'Manual Entry',
  estimatedValue: '',
  rangeLow: '',
  rangeHigh: '',
  confidence: 'Medium',
  notes: '',
};

function EntryCard({ entry, index, onUpdate, onRemove, isEditing, onStartEdit, onCancelEdit }) {
  const [form, setForm] = useState(entry);

  useEffect(() => { setForm(entry); }, [entry]);

  const handleSave = () => {
    onUpdate(index, {
      ...form,
      estimatedValue: form.estimatedValue ? parseInt(form.estimatedValue, 10) || 0 : null,
      rangeLow: form.rangeLow ? parseInt(form.rangeLow, 10) || 0 : null,
      rangeHigh: form.rangeHigh ? parseInt(form.rangeHigh, 10) || 0 : null,
    });
  };

  const inputClass = "w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 bg-white";

  if (isEditing) {
    return (
      <div className="p-3 rounded-lg border-2 border-blue-300 bg-blue-50/30 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
            <input type="date" value={form.date ? form.date.split('T')[0] : ''} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Source</label>
            <input type="text" value={form.source || ''} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="e.g., PlanePortal, VREF" className={inputClass} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Estimated Value</label>
            <input type="number" value={form.estimatedValue || ''} onChange={(e) => setForm({ ...form, estimatedValue: e.target.value })} placeholder="$" className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Range Low</label>
            <input type="number" value={form.rangeLow || ''} onChange={(e) => setForm({ ...form, rangeLow: e.target.value })} placeholder="$" className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Range High</label>
            <input type="number" value={form.rangeHigh || ''} onChange={(e) => setForm({ ...form, rangeHigh: e.target.value })} placeholder="$" className={inputClass} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Confidence</label>
            <select value={form.confidence || ''} onChange={(e) => setForm({ ...form, confidence: e.target.value })} className={inputClass}>
              <option value="">—</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
            <input type="text" value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" className={inputClass} />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="text" size="sm" className="flex items-center gap-1 py-1 px-2 text-xs" onClick={onCancelEdit}>
            <XMarkIcon className="h-3.5 w-3.5" /> Cancel
          </Button>
          <Button size="sm" className="flex items-center gap-1 py-1 px-2 text-xs" onClick={handleSave}>
            <CheckIcon className="h-3.5 w-3.5" /> Save
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="group p-3 rounded-lg border border-gray-200 hover:border-blue-200 hover:bg-blue-50/20 transition-all">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Typography variant="small" className="font-semibold text-gray-800">
              {formatCurrency(entry.estimatedValue)}
            </Typography>
            {entry.confidence && (
              <Chip
                size="sm"
                value={entry.confidence}
                color={CONFIDENCE_COLORS[entry.confidence] || 'gray'}
                variant="ghost"
                className="text-[10px] py-0 px-1.5"
              />
            )}
          </div>
          {(entry.rangeLow || entry.rangeHigh) && (
            <Typography variant="small" className="text-xs text-gray-500">
              Range: {formatCurrency(entry.rangeLow)} — {formatCurrency(entry.rangeHigh)}
            </Typography>
          )}
          <div className="flex items-center gap-3 mt-1.5">
            <Typography variant="small" className="text-xs text-gray-400">
              {formatDate(entry.date)}
            </Typography>
            <Typography variant="small" className="text-xs text-gray-400">
              {entry.source || '—'}
            </Typography>
          </div>
          {entry.notes && (
            <Typography variant="small" className="text-xs text-gray-400 italic mt-1">
              {entry.notes}
            </Typography>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
          <button onClick={() => onStartEdit(index)} className="p-1 rounded hover:bg-gray-200 transition-colors" title="Edit">
            <PencilIcon className="h-3.5 w-3.5 text-gray-400" />
          </button>
          <button onClick={() => onRemove(index)} className="p-1 rounded hover:bg-red-100 transition-colors" title="Remove">
            <TrashIcon className="h-3.5 w-3.5 text-gray-400 hover:text-red-500" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ValuationHistorySection({ content, originalContent, onSave, onReset, isSaving }) {
  const [entries, setEntries] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setEntries(content?.entries || []);
    setHasChanges(false);
    setEditingIndex(null);
  }, [content?.entries]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateEntry = (index, updatedEntry) => {
    setEntries((prev) => {
      const updated = [...prev];
      updated[index] = updatedEntry;
      return updated;
    });
    setHasChanges(true);
    setEditingIndex(null);
  };

  const addEntry = () => {
    setEntries((prev) => [...prev, { ...EMPTY_ENTRY }]);
    setEditingIndex(entries.length);
    setHasChanges(true);
  };

  const removeEntry = (index) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
    setHasChanges(true);
    if (editingIndex === index) setEditingIndex(null);
  };

  const handleSave = () => {
    if (onSave) onSave({ entries });
  };

  const handleReset = () => {
    if (onReset) {
      onReset();
    } else {
      setEntries(content?.entries || []);
    }
    setHasChanges(false);
    setEditingIndex(null);
  };

  return (
    <Card className="h-full flex flex-col">
      <CardBody className="flex-1 flex flex-col p-4 overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <Typography variant="h6" color="blue-gray">
              Valuation History
            </Typography>
            <Typography variant="small" className="text-gray-500 text-xs">
              Track valuations over time
            </Typography>
          </div>
        </div>

        {entries.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-8">
            <Typography variant="small" className="mb-2">No valuation history yet</Typography>
            <Typography variant="small" className="text-xs text-center mb-4 max-w-[250px]">
              Regenerate the report to auto-populate from previous valuations, or add entries manually.
            </Typography>
          </div>
        ) : (
          <div className="flex-1 space-y-2 overflow-auto">
            {entries.map((entry, i) => (
              <EntryCard
                key={i}
                entry={entry}
                index={i}
                onUpdate={updateEntry}
                onRemove={removeEntry}
                isEditing={editingIndex === i}
                onStartEdit={setEditingIndex}
                onCancelEdit={() => setEditingIndex(null)}
              />
            ))}
          </div>
        )}

        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <Button
              variant="outlined"
              size="sm"
              className="flex items-center gap-1 py-1.5 px-3 text-xs"
              onClick={addEntry}
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add Entry
            </Button>
            {hasChanges && (
              <div className="flex gap-2">
                <Button variant="text" size="sm" onClick={handleReset} disabled={isSaving} className="py-1.5 px-3 text-xs">
                  Reset
                </Button>
                <Button size="sm" onClick={handleSave} disabled={isSaving} className="py-1.5 px-3 text-xs">
                  {isSaving ? <Spinner className="h-3.5 w-3.5 mr-1" /> : null}
                  Save Changes
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

export default ValuationHistorySection;
